// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT HERE.
//
// Source of truth: invits-app-new/shared/rsvpGate.js
// Regenerate:     npm run sync:shared   (from invits-app-new)
//
// Editing this copy reintroduces exactly the app/web divergence that shared/
// exists to eliminate. Change the source, then re-run the sync.
// ─────────────────────────────────────────────────────────────────────────────

// src/features/events/rsvpGate.js
//
// Decides what the RSVP area should render. Pure function, no I/O, no hooks —
// so it can be reasoned about and tested directly.
//
// ── Why this file exists ────────────────────────────────────────────────────
//
// The old logic was a single boolean expression in app/event/[id].js:
//
//     {user && !isUserHost && !isUserCohost && canAccessContent() && ( ... )}
//     const canAccessContent = () =>
//       accessInfo?.fullAccess || isUserHost || isUserCohost;
//
// `accessInfo` was plain useState populated by the 5th call in a sequential
// waterfall of 7 network requests, wrapped in one try/catch, with no retry. If
// that request failed or was still in flight, `accessInfo` was null, so
// `canAccessContent()` returned false and the RSVP block rendered NOTHING —
// indistinguishable from "you are not allowed to RSVP". The event body had
// already rendered, so the screen looked fine and complete. The user simply had
// no button, no error, no retry, and no way back short of killing the app.
//
// That is the "RSVP sometimes doesn't show" bug, and it is device- and
// network-dependent because it is a race against a flaky request.
//
// The fix is to make "we don't know yet" and "we failed to find out" into
// first-class states that the UI is FORCED to handle, rather than letting them
// collapse into the same falsy value as "not allowed".

/**
 * @typedef {'loading'|'error'|'hidden'|'locked'|'available'} RsvpGateKind
 *
 * @typedef {{
 *   kind: RsvpGateKind,
 *   reason?: string,
 *   error?: unknown,
 *   lockedBy?: 'guestList' | 'approval' | 'pending' | 'rejected' | 'capacity' | 'payment',
 *   canRequestAccess?: boolean,
 *   canJoinWaitlist?: boolean,
 * }} RsvpGate
 */

/**
 * @param {{
 *   isAuthed: boolean,
 *   accessStatus: 'pending'|'success'|'error',
 *   access: any,
 *   accessError?: unknown,
 *   event: any,
 *   capacity?: { isFull?: boolean } | null,
 *   requiresPayment?: boolean,
 * }} input
 * @returns {RsvpGate}
 */
export function resolveRsvpGate({
  isAuthed,
  accessStatus,
  access,
  accessError,
  event,
  capacity,
  requiresPayment,
}) {
  // Signed-out viewers get a real call to action, not a blank space. This was
  // previously just `user &&`, which rendered nothing at all for logged-out
  // users opening a shared invite link — the single most common entry point.
  if (!isAuthed) {
    return { kind: 'locked', lockedBy: 'approval', reason: 'signedOut' };
  }

  if (!event) return { kind: 'loading' };

  // A host or cohost genuinely has no RSVP UI — they are running the event.
  // Checked before the access query so hosts never wait on it.
  if (access?.isHost || access?.isCohost) {
    return { kind: 'hidden', reason: 'host' };
  }

  // ── The states the old code could not express ───────────────────────────
  if (accessStatus === 'pending') return { kind: 'loading' };
  if (accessStatus === 'error') {
    return { kind: 'error', error: accessError, reason: 'accessCheckFailed' };
  }

  // From here on the access verdict is genuinely known.
  if (!access) {
    // Resolved successfully but empty: treat as a failure to determine, not as
    // a denial. Fail loud, never fail silent.
    return { kind: 'error', reason: 'accessMissing' };
  }

  if (access.fullAccess) {
    if (requiresPayment) {
      return { kind: 'locked', lockedBy: 'payment', reason: 'ticketRequired' };
    }
    if (capacity?.isFull) {
      return { kind: 'locked', lockedBy: 'capacity', canJoinWaitlist: true, reason: 'atCapacity' };
    }
    return { kind: 'available' };
  }

  // No full access: say precisely why, and offer the matching action.
  if (access.requiresApproval) {
    if (access.accessStatus === 'pending')  return { kind: 'locked', lockedBy: 'pending' };
    if (access.accessStatus === 'rejected') return { kind: 'locked', lockedBy: 'rejected' };
    return { kind: 'locked', lockedBy: 'approval', canRequestAccess: true };
  }

  if (access.requiresGuestList) {
    return { kind: 'locked', lockedBy: 'guestList', canRequestAccess: true };
  }

  return { kind: 'locked', lockedBy: 'approval', canRequestAccess: true };
}

/** Copy for each locked reason, kept beside the logic it describes. */
export const LOCK_COPY = {
  signedOut: {
    title: 'Sign in to RSVP',
    body: 'You need an account to let the host know you are coming.',
    action: 'Sign in',
  },
  guestList: {
    title: 'Guest list only',
    body: 'This invite is limited to the host’s guest list. Ask them to add you.',
    action: 'Request access',
  },
  approval: {
    title: 'Host approval needed',
    body: 'Send a request and the host will let you in.',
    action: 'Request access',
  },
  pending: {
    title: 'Request sent',
    body: 'Waiting on the host. We will notify you the moment they respond.',
    action: null,
  },
  rejected: {
    title: 'Not approved',
    body: 'The host did not approve this request.',
    action: null,
  },
  capacity: {
    title: 'This event is full',
    body: 'Join the waitlist and we will tell you if a spot opens.',
    action: 'Join waitlist',
  },
  payment: {
    title: 'Ticket required',
    body: 'Grab a pass to confirm your spot.',
    action: 'Get a pass',
  },
};
